import { defineComponent as _defineComponent } from 'vue'
import CcModal from '@/views/Automation/CampaignCreatorV2/components/Modals/CcModal.ts.vue';
import { pluralize } from '@sh/helpers';
import { api, apiStore } from '@sh/services/api';
import { ComplianceProgram, SearchFeed } from '@sh/types';
import { uniq } from 'lodash';
import { computed, ref } from 'vue';

interface Dropdown<Option> {
  selected: NullableType<Option>;
  options: Option[];
}

interface Emits {
  (
    event: 'onNotification',
    message: string,
    type: string,
    metadata?: Partial<ComplianceProgram> & { action: string }
  ): void;
}

interface Props {
  entities: Array<{
    id: string;
    compliance_revenue_stream_type: string;
    revenue_streaming_sharing_compliance: string;
    traffic_source_content_id: string;
  }>;
  campaignId?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ComplianceModal.ts',
  props: {
    entities: null,
    campaignId: null
  },
  emits: ["onNotification"],
  setup(__props: any, { expose, emit: emits }: { emit: ({
  (
    event: 'onNotification',
    message: string,
    type: string,
    metadata?: Partial<ComplianceProgram> & { action: string }
  ): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  entities: Array<{
    id: string;
    compliance_revenue_stream_type: string;
    revenue_streaming_sharing_compliance: string;
    traffic_source_content_id: string;
  }>;
  campaignId?: string;
};





const isModalOpen = ref(false);

const isLoading = ref(false);

const userSettings = ref<NullableType<SearchFeed.UserResponse>>(null);

const emailDropdown = ref<Dropdown<SelectOption<string>>>({
  selected: null,
  options: [],
});

const searchFeedDropdown = ref<Dropdown<SelectOption<string>>>({
  selected: null,
  options: [],
});

const action = ref('');
const entity = ref('');
const title = ref('');

const titleInfo = computed<string>(() => {
  switch (action.value) {
    case 'allow':
      return 'The creative details that will be visible to the search feed provider are limited to: creative image or vide, text, destination url, traffic source clicks, search feed clicks. The search feed provider will not be able to see any other information about your campaign (like budgets, bids, targeting, spending amount, revenue etc).';
    case 'remove':
      return 'Once the operation is completed the search feed provider will not be able to see the creatives of the selected campaigns anymore.';
    default:
      return '';
  }
});

function validateData(data: Partial<ComplianceProgram>): data is ComplianceProgram {
  if (!data.type) {
    return false;
  }

  if (!data.email) {
    return false;
  }

  return true;
}

const complianceProgram = computed<NullableType<ComplianceProgram>>(() => {
  const data = {
    email: emailDropdown.value.selected?.value,
    type: searchFeedDropdown.value.selected?.value,
  };

  if (validateData(data)) {
    return data;
  }

  return null;
});

const save = computed(() => {
  const label = action.value === 'allow' ? 'Save' : 'Remove';
  const loadingMessage = isLoading.value ? 'Loading please wait' : '';
  const requiredMessage = complianceProgram.value
    ? ''
    : 'Please select search feed provider and email to save changes.';
  const disabledMessage = label === 'Remove' ? '' : loadingMessage || requiredMessage;

  return {
    label,
    disabledMessage,
  };
});

function onModalClose() {
  isModalOpen.value = false;
  isLoading.value = false;
  emailDropdown.value.selected = { content: '', value: '' };
  searchFeedDropdown.value.selected = { content: '', value: '' };
  action.value = '';
  entity.value = '';
  title.value = '';
}

async function onSearchFeedChange(event: SelectOption<string>) {
  searchFeedDropdown.value.selected = event;
  emailDropdown.value.selected = null;
  emailDropdown.value.options =
    userSettings.value?.settings.compliance?.[event.value]?.email?.map((email) => ({
      content: email,
      value: email,
    })) ?? [];
}

function autoSelectEmailAndSearchFeed() {
  const uniqueSearchFeed = uniq(props.entities.flatMap((entity) => entity.compliance_revenue_stream_type)).filter(
    (email) => email !== '(not set)'
  );
  const uniqueEmails = uniq(props.entities.flatMap((entity) => entity.revenue_streaming_sharing_compliance)).filter(
    (email) => email !== '(not set)'
  );

  if (uniqueEmails.length === 1 && uniqueSearchFeed.length === 1) {
    const selectedSearchFeed = searchFeedDropdown.value.options.find((option) => option.value === uniqueSearchFeed[0]);

    if (selectedSearchFeed) {
      emailDropdown.value.options =
        userSettings.value?.settings.compliance?.[selectedSearchFeed?.content].email.map((email) => ({
          content: email,
          value: email,
        })) ?? [];

      const selectedEmail = emailDropdown.value.options.find((option) => option.value === uniqueEmails[0]);

      if (selectedEmail) {
        emailDropdown.value.selected = selectedEmail;
        searchFeedDropdown.value.selected = selectedSearchFeed;
      }
    }
  } else if (uniqueEmails.length > 1 && uniqueSearchFeed.length > 1 && action.value === 'allow') {
    onModalClose();
    emits(
      'onNotification',
      'Multiple emails or search feeds found on the same actions, please select one email!',
      'error'
    );
  }
}

function setActionAndEntity(newAction: string, newEntity: string) {
  if (newAction && newEntity) {
    action.value = newAction;
    entity.value = newEntity;

    if (entity.value === 'campaign') {
      if (action.value === 'allow') {
        title.value = `Share creatives of selected campaigns with your search feed provider.`;
      } else {
        title.value = `Remove access to search feed provider from all creatives of selected campaigns.`;
      }
    }

    if (entity.value === 'content') {
      if (action.value === 'allow') {
        title.value = `Share creatives with your search feed provider.`;
      } else {
        title.value = `Remove access to search feed provider from all of selected creatives.`;
      }
    }
  }
}

async function onSaveClick() {
  isLoading.value = true;
  try {
    const email = emailDropdown.value.selected?.value || '(not set)';
    const type = searchFeedDropdown.value.selected?.value || '(not set)';

    let result;

    if (entity.value === 'campaign') {
      result = await api.campaigns.updateCampaignCompliance(
        props.entities.map((item) => item.id),
        action.value.toLowerCase() === 'allow',
        email,
        type
      );
    } else {
      if (!props.campaignId) {
        emits('onNotification', 'Campaign ID is required!', 'error');
        return;
      }
      result = await api.campaigns.updateContentsCompliance(
        props.entities.map((item) => item.traffic_source_content_id),
        action.value.toLowerCase() === 'allow',
        email,
        props.campaignId,
        type
      );
    }
    emits('onNotification', result.message || 'Successfully saved changes.', 'success', {
      action: action.value,
      email,
      type,
    });
  } catch (error: any) {
    emits('onNotification', error?.message || 'Something went wrong, please try again later!', 'error');
  }
  isLoading.value = false;
  onModalClose();
}

async function show(action: string, entity: string) {
  try {
    isLoading.value = true;
    isModalOpen.value = true;
    setActionAndEntity(action, entity);
    userSettings.value = await apiStore.trafficSources.users();
    const findUniqueSearchFeed = Object.values(userSettings.value.settings.compliance ?? {}).map((item) => item.type);
    searchFeedDropdown.value.options = findUniqueSearchFeed.map((item) => ({
      content: item,
      value: item,
    }));
    autoSelectEmailAndSearchFeed();
  } catch (error: any) {
    emits('onNotification', error?.message || 'Something went wrong, please try again later!', 'error');
  }
  isLoading.value = false;
}

expose({
  show,
});

return { __sfc: true,emits, props, isModalOpen, isLoading, userSettings, emailDropdown, searchFeedDropdown, action, entity, title, titleInfo, validateData, complianceProgram, save, onModalClose, onSearchFeedChange, autoSelectEmailAndSearchFeed, setActionAndEntity, onSaveClick, show, CcModal, pluralize }
}

})